import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { streamChat } from "@/lib/api";

const STARTERS = [
    "¿Qué hace exactamente Scalexa?",
    "¿Cómo medís el ROI?",
    "¿Dónde fallan la mayoría de proyectos B2B de IA?",
];

export default function Chatbot() {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState([]);
    const [streaming, setStreaming] = useState(false);
    const sessionRef = useRef(null);
    const bottomRef = useRef(null);
    const abortRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, open]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || streaming) return;
        setInput("");
        setMessages((m) => [
            ...m,
            { role: "user", content: msg },
            { role: "assistant", content: "" },
        ]);
        setStreaming(true);
        abortRef.current = new AbortController();
        try {
            await streamChat({
                message: msg,
                sessionId: sessionRef.current,
                signal: abortRef.current.signal,
                onEvent: (ev) => {
                    if (ev.type === "session") {
                        sessionRef.current = ev.session_id;
                    } else if (ev.type === "delta") {
                        setMessages((m) => {
                            const copy = [...m];
                            const last = copy[copy.length - 1];
                            if (last && last.role === "assistant") {
                                copy[copy.length - 1] = {
                                    ...last,
                                    content: last.content + ev.content,
                                };
                            }
                            return copy;
                        });
                    } else if (ev.type === "error") {
                        setMessages((m) => {
                            const copy = [...m];
                            copy[copy.length - 1] = {
                                role: "assistant",
                                content:
                                    "Algo interrumpió la respuesta. Inténtalo de nuevo o usa la página de Contacto.",
                            };
                            return copy;
                        });
                    }
                },
            });
        } catch (e) {
            setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant" && !last.content) {
                    copy[copy.length - 1] = {
                        role: "assistant",
                        content:
                            "Problema de red. Inténtalo de nuevo o escríbenos desde la página de Contacto.",
                    };
                }
                return copy;
            });
        } finally {
            setStreaming(false);
        }
    };

    return (
        <>
            <button
                data-testid="chatbot-trigger"
                onClick={() => setOpen(true)}
                className={`fixed bottom-24 right-6 z-30 flex items-center gap-3 bg-brand-navy text-white px-5 py-4 shadow-[0_18px_44px_rgba(8,37,109,0.25)] hover:bg-brand-electric transition-colors duration-300 ${
                    open ? "opacity-0 pointer-events-none" : "opacity-100"
                }`}
            >
                <span className="relative flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-dot" />
                </span>
                <span className="font-medium tracking-wide">Pregunta a Scalexa AI</span>
                <MessageSquare size={16} />
            </button>

            {open && (
                <div
                    data-testid="chatbot-panel"
                    className="fixed bottom-0 right-0 md:bottom-6 md:right-6 z-40 w-full md:w-[420px] h-[600px] max-h-[100vh] bg-white border border-brand-line shadow-[0_30px_80px_rgba(8,37,109,0.25)] flex flex-col"
                >
                    <div className="bg-brand-navy text-white px-5 py-4 flex items-center justify-between">
                        <div>
                            <div className="label-mono !text-white/60">Scalexa · IA en vivo</div>
                            <div className="font-heading text-lg mt-1">Pregúntanos lo que sea</div>
                        </div>
                        <button
                            data-testid="chatbot-close"
                            onClick={() => setOpen(false)}
                            className="text-white/80 hover:text-white"
                            aria-label="Cerrar chat"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-5 py-6 space-y-5 bg-brand-soft">
                        {messages.length === 0 && (
                            <div>
                                <p className="text-sm text-brand-gray leading-relaxed">
                                    Estoy entrenado en la práctica de Sistemas de
                                    Ingresos con IA de Scalexa. Pregúntame por nuestro
                                    enfoque, colaboraciones o cómo medimos el impacto.
                                </p>
                                <div className="mt-5 space-y-2">
                                    {STARTERS.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => send(s)}
                                            data-testid={`chatbot-starter-${s
                                                .slice(0, 12)
                                                .replace(/\s+/g, "-")
                                                .toLowerCase()}`}
                                            className="block w-full text-left text-sm border border-brand-line bg-white px-4 py-3 hover:border-brand-electric hover:text-brand-navy transition-colors text-brand-gray"
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`flex ${
                                    m.role === "user" ? "justify-end" : "justify-start"
                                }`}
                            >
                                <div
                                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                                        m.role === "user"
                                            ? "bg-brand-navy text-white"
                                            : "bg-white text-brand-navy border border-brand-line"
                                    }`}
                                    data-testid={`chatbot-msg-${m.role}-${i}`}
                                >
                                    {m.content || (streaming ? (
                                        <Loader2 className="animate-spin" size={14} />
                                    ) : "")}
                                </div>
                            </div>
                        ))}
                        <div ref={bottomRef} />
                    </div>

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            send();
                        }}
                        className="border-t border-brand-line bg-white px-4 py-3 flex items-center gap-3"
                    >
                        <input
                            data-testid="chatbot-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Escribe tu pregunta…"
                            className="flex-1 bg-transparent text-sm text-brand-navy placeholder:text-brand-gray/60 focus:outline-none"
                            disabled={streaming}
                        />
                        <button
                            type="submit"
                            data-testid="chatbot-send"
                            disabled={streaming || !input.trim()}
                            className="bg-brand-electric text-white p-2.5 hover:bg-brand-navy transition-colors disabled:opacity-40"
                            aria-label="Enviar"
                        >
                            {streaming ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
